import { type FC, type PropsWithChildren, useMemo } from "react";
import { createMongoAbility } from "@casl/ability";
import { unpackRules } from "@casl/ability/extra";
import {
  AbilityContext,
  CurrentOrganizationRolesContext,
  type AppAbility,
} from "@/partner/shared/lib";
import type { UserType } from "@/types/graphql/partner";

export interface AbilityProviderProps extends PropsWithChildren {
  user: UserType;
}

/**
 * Пересобирает ability при смене `user.abilityRules`/
 * `user.currentOrganizationRoles`. Отдельного триггера на смену
 * организации не нужно: `UpdateCurrentUserMutation.graphql` запрашивает
 * те же поля, и Apollo InMemoryCache сам мержит их в нормализованную
 * запись `User` по `id` — тот же объект, на который смотрит
 * `useCurrentUserQuery()` в `AuthProvider` (app/providers), так что новый
 * `user` (и новый `ability`) приходит без явного рефетча. Кэш СПИСКОВ
 * других разделов (продукты, контент, сотрудники...) при этом остаётся
 * от старой организации — это закрывает `UserSettings.tsx`
 * (`window.location.reload()` при смене `currentOrganizationId`, как в
 * `useSignOut`), а не этот провайдер.
 */
export const AbilityProvider: FC<AbilityProviderProps> = ({
  user,
  children,
}) => {
  const ability = useMemo<AppAbility>(
    () => createMongoAbility(unpackRules(user.abilityRules ?? [])),
    [user.abilityRules]
  );

  return (
    <AbilityContext.Provider value={ability}>
      <CurrentOrganizationRolesContext.Provider
        value={user.currentOrganizationRoles ?? []}
      >
        {children}
      </CurrentOrganizationRolesContext.Provider>
    </AbilityContext.Provider>
  );
};
